/**
 * "2 hours ago" from an ISO timestamp, in the reader's language.
 *
 * `Intl.RelativeTimeFormat` and not a hand-written table: the branch counts differ per
 * language (Slovak has three plural forms, Czech four), and a table written against English
 * gets them wrong the moment a second language ships.
 *
 * The locale passed in is the reader's chosen one, which is what `useFormats()` hands it.
 * It lives in `lib/i18n` and not next to a screen because every screen family formats a
 * moment and none of them owns the rule for it.
 */

interface Division {
  readonly amount: number;
  readonly unit: Intl.RelativeTimeFormatUnit;
}

/** Each step is "how many of me make one of the next". Ordered smallest first. */
const DIVISIONS: readonly Division[] = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  // Average weeks per month. The value is only ever used to decide which unit reads best,
  // never to compute a date.
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

/** One formatter per locale. Constructing one is expensive and a card grid draws many. */
const formatters = new Map<string, Intl.RelativeTimeFormat>();

function formatterFor(locale: string): Intl.RelativeTimeFormat {
  const cached = formatters.get(locale);
  if (cached !== undefined) return cached;
  // `numeric: 'auto'` is what produces "yesterday" instead of "1 day ago" in the languages
  // that have a word for it.
  const created = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  formatters.set(locale, created);
  return created;
}

export function relativeTime(iso: string, locale: string, now: number = Date.now()): string {
  const timestamp = new Date(iso).getTime();
  // A timestamp the browser cannot parse is a server contract problem, not something to crash
  // a card over; the raw value is at least honest.
  if (Number.isNaN(timestamp)) return iso;

  let delta = (timestamp - now) / 1000;
  for (const division of DIVISIONS) {
    if (Math.abs(delta) < division.amount) {
      return formatterFor(locale).format(Math.round(delta), division.unit);
    }
    delta /= division.amount;
  }
  return formatterFor(locale).format(Math.round(delta), 'year');
}
